import * as React from 'react'
import {
  CarouselBase as Carousel,
  CarouselContentBase as CarouselContent,
  CarouselItemBase as CarouselItem,
  CarouselPreviousBase as CarouselPrevious,
  CarouselNextBase as CarouselNext,
  type CarouselApi,
} from '@/components/ui/carousel'
import { cn } from '@/lib/utils'

/**
 * Figma (node 399:1140 — Carousel):
 *
 * Layouts:
 *   Single slide: basis-full
 *   2-up:  basis-1/2
 *   3-up:  basis-1/3
 *   4-up:  basis-1/4
 *   Vertical: orientation="vertical"
 *
 * Nav buttons (node 399:1140):
 *   h-8 w-8 rounded-full, border border-input, bg-background
 *   Horizontal: < > (left/right of carousel)
 *   Vertical: ^ v (top/bottom of carousel)
 *   States: Default, Hover (bg-accent), Focus (ring-ring), Disabled (opacity-50), Pressed
 *
 * Slide counter: "Slide 1 of 5" — text-sm text-muted-foreground, centered below
 *
 * Content: overflow-hidden, gap-4 (pl-4 / pt-4)
 * Items: rounded-lg border bg-card
 */

/* ─── Root (wrapped to avoid mutating Shadcn displayName) ─── */

const IntuiCarousel = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof Carousel>
>((props, ref) => <Carousel ref={ref} {...props} />)
IntuiCarousel.displayName = 'IntuiCarousel'

/* ─── Content ─── */

const IntuiCarouselContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof CarouselContent>
>((props, ref) => <CarouselContent ref={ref} {...props} />)
IntuiCarouselContent.displayName = 'IntuiCarouselContent'

/* ─── IntuiCarouselItem ─── */

export interface IntuiCarouselItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of items visible at once (controls basis) */
  slidesPerView?: 1 | 2 | 3 | 4
}

const basisMap: Record<number, string> = {
  1: 'basis-full',
  2: 'basis-1/2',
  3: 'basis-1/3',
  4: 'basis-1/4',
}

const IntuiCarouselItem = React.forwardRef<HTMLDivElement, IntuiCarouselItemProps>(
  ({ className, slidesPerView = 1, ...props }, ref) => (
    <CarouselItem
      ref={ref}
      className={cn(basisMap[slidesPerView], className)}
      {...props}
    />
  ),
)
IntuiCarouselItem.displayName = 'IntuiCarouselItem'

/* ─── IntuiCarouselPrevious ─── */

const IntuiCarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof CarouselPrevious>
>(({ className, ...props }, ref) => (
  <CarouselPrevious
    ref={ref}
    className={cn('h-8 w-8 rounded-full', className)}
    {...props}
  />
))
IntuiCarouselPrevious.displayName = 'IntuiCarouselPrevious'

/* ─── IntuiCarouselNext ─── */

const IntuiCarouselNext = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof CarouselNext>
>(({ className, ...props }, ref) => (
  <CarouselNext
    ref={ref}
    className={cn('h-8 w-8 rounded-full', className)}
    {...props}
  />
))
IntuiCarouselNext.displayName = 'IntuiCarouselNext'

/**
 * Hook to track carousel slide index. Pass setApi to <IntuiCarousel setApi={setApi}>.
 */
export function useCarouselCounter() {
  const [api, setApi] = React.useState<CarouselApi>()
  const [current, setCurrent] = React.useState(0)
  const [count, setCount] = React.useState(0)

  React.useEffect(() => {
    if (!api) return

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap() + 1)
    }

    const onReInit = () => {
      setCount(api.scrollSnapList().length)
      onSelect()
    }

    onReInit()

    api.on('select', onSelect)
    api.on('reInit', onReInit)
    return () => {
      api.off('select', onSelect)
      api.off('reInit', onReInit)
    }
  }, [api])

  return { current, count, setApi }
}

export {
  IntuiCarousel,
  IntuiCarouselContent,
  IntuiCarouselItem,
  IntuiCarouselPrevious,
  IntuiCarouselNext,
}
export type { CarouselApi }
